/**
 * Level 2 — eight things Turo teaches, and the child copies.
 *
 * 🔴 THERE IS NO INPUT OF ANY KIND. No tapping, no tiles, no camera, no
 * microphone. Turo names a situation, teaches ONE greeting, performs it, and
 * waits while the child does it with him. Then the next one.
 *
 * ⚠️ THAT IS A NARROWER LEVEL THAN THE PLAN'S, AND THE TRADE IS WORTH STATING.
 * `TURO_WORLD_L2_L3_PLAN.md` §3.3 built the level around a CHOOSE beat — *"no
 * wrong answer… the lesson is that the choice is theirs and it is honoured"* —
 * and choosing is how §1 says autonomy is taught. A watch-and-copy level cannot
 * teach that, because there is nothing to choose.
 *
 * 📋 WHAT THAT COSTS, PRECISELY: the hug scenario can still SAY *"you can always
 * choose whether you want one"*, and the toy scenario can still say *"you can
 * say not yet"* — but the child never exercises either. §1's rule survives as a
 * sentence rather than as practice. If the choose mechanic ever returns, those
 * two are where it belongs first.
 *
 * ⚠️ EVERY SCENARIO TEACHES EXACTLY ONE THING. Offering alternatives out loud
 * with no way to pick between them would be asking a question and then answering
 * it — worse than not asking.
 */

export interface L2Scenario {
  id: string;
  /** The illustrated situation. */
  card: string;
  /** Framing, spoken before the lesson. */
  open: readonly string[];
  /** The one thing this scenario teaches. */
  teach: string;
  /** The gesture Turo performs, from the L2 atlas. */
  poseId: string;
  /** "Now do it with me…" — then the pause. */
  practise: string;
  /** Said after the pause. The lesson, never a verdict. */
  close: readonly string[];
}

export const L2_SCENARIOS: readonly L2Scenario[] = [
  {
    id: "l2-friend",
    card: "card_friend_arrives",
    open: ["sc.friend.open"],
    teach: "sc.teach.friend",
    poseId: "wave_hello",
    practise: "sc.do.wave",
    close: ["sc.friend.close"],
  },
  {
    /**
     * 🔴 THE TEACHER IS ANSWERED WITH HER OWN WORDS. She says "good morning",
     * so the child says "good morning" back — matching a greeting is the first
     * turn-taking a child learns, and it is what actually happens at a school
     * gate. Namaste stays with the family elders below, where the child is
     * taught it as the greeting for Appa and for grandparents.
     */
    id: "l2-teacher",
    card: "card_school_gate_morning",
    open: ["sc.teacher.open", "sc.teacher.says"],
    teach: "sc.teach.teacher",
    poseId: "wave_hello",
    practise: "sc.do.goodmorning",
    close: ["sc.teacher.close"],
  },
  {
    /**
     * 🔴 THE HUG IS TAUGHT WITH ITS CONDITION ATTACHED, IN ONE BREATH.
     * §3.3 keeps the hug in the level because *"a child who wants a hug must be
     * able to choose one, or the level teaches that affection is suspect"* — but
     * with nothing to choose, the permission has to be carried by the words.
     * So the close says it out loud, every time, to every child.
     */
    id: "l2-mother",
    card: "card_biscuit",
    open: ["sc.mother.open", "sc.mother.says"],
    teach: "sc.teach.mother",
    poseId: "open_arms",
    practise: "sc.do.hug",
    close: ["sc.mother.close"],
  },
  {
    id: "l2-father",
    card: "card_evening_home",
    open: ["sc.father.open", "sc.father.says"],
    teach: "sc.teach.father",
    poseId: "namaste",
    practise: "sc.do.namaste",
    close: ["sc.father.close"],
  },
  {
    id: "l2-elder",
    card: "card_grandmother",
    open: ["sc.elder.open", "sc.elder.says"],
    teach: "sc.teach.elder",
    poseId: "namaste",
    practise: "sc.do.namaste",
    close: ["sc.elder.close"],
  },
  {
    id: "l2-help",
    card: "card_shoelace",
    open: ["sc.help.open"],
    teach: "sc.teach.help",
    /** Hand raised — the gesture that goes with asking. */
    poseId: "high_five",
    practise: "sc.do.askhelp",
    close: ["sc.help.close"],
  },
  {
    /**
     * 🔴 "NOT YET" IS TAUGHT AS A GREETING'S EQUAL, NOT AS A LAST RESORT.
     * §1: courtesy must never be the correct answer to a boundary. Sharing is
     * not the lesson here — having words for either answer is.
     */
    id: "l2-toy",
    card: "card_crayon",
    open: ["sc.toy.open"],
    teach: "sc.teach.toy",
    poseId: "no_thank_you",
    practise: "sc.do.notyet",
    close: ["sc.toy.close"],
  },
  {
    id: "l2-final",
    /* Someone ARRIVING to say hello — `card_friend_leaving` is L3's bye-bye. */
    card: "card_friend_arrives",
    open: ["sc.final.open"],
    teach: "sc.teach.final",
    poseId: "wave_hello",
    practise: "sc.do.wave",
    close: ["sc.final.close", "sc.final.praise"],
  },
];

export const l2ScenarioById = (id: string): L2Scenario | undefined =>
  L2_SCENARIOS.find((s) => s.id === id);
